import React, { useState, useEffect } from "react";
import { BarChart3, Star, Lightbulb, ArrowRight } from "lucide-react";
import SkillsGapAnalysisToolModal from "../components/skill-gap-analysis/SkillsGapAnalysisToolModal";
import ViewAnalysisModal from "../components/skill-gap-analysis/ViewAnalysisModal";
import AssessmentHistory from "../components/skill-gap-analysis/AssessmentHistory";
import { COLORS } from "../constants/colors";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";

const SkillGapAnalysisPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [previousReports, setPreviousReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSkillGapAnalyses();
  }, []);

  const calculateOverallScore = (matchingCount, gapCount) => {
    const totalSkills = matchingCount + gapCount;
    if (totalSkills === 0) return 0;
    return Math.round((matchingCount / totalSkills) * 100);
  };

  const calculateTrend = (matchingCount, gapCount) => {
    if (matchingCount === 0 && gapCount === 0) return "stable";
    if (matchingCount > gapCount) return "up"; // More matching than gaps = positive trend
    if (matchingCount < gapCount) return "down";
    return "stable"; 
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "bg-green-400";
    if (score >= 60) return "bg-yellow-400";
    return "bg-red-400";
  };

  const handleStartAnalysis = () => setIsModalOpen(true);
  
  const handleViewReport = (report) => {
    if (report.rawData) {
      setSelectedReport(report.rawData);
      setIsViewModalOpen(true);
    } else {
      console.error("No raw data available for report:", report);
      alert("Unable to view this report - data not available");
    }
  };
  
  const handleDownloadReport = (report) => {
    if (!report.rawData) {
      alert("No data available for download");
      return;
    }

    try {
      const { matchingSkills, skillsGap, targetOccupation } = report.rawData;
      
      const excelData = [
        ["Matching Skills", "Gap Skills"]
      ];
      
      const maxLength = Math.max(
        matchingSkills?.length || 0,
        skillsGap?.length || 0
      );
      
      for (let i = 0; i < maxLength; i++) {
        const matchingSkill = matchingSkills?.[i] || "";
        const gapSkill = skillsGap?.[i] || "";
        excelData.push([matchingSkill, gapSkill]);
      }
      
      const csvContent = excelData.map(row => 
        row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Skills_Analysis_${targetOccupation?.replace(/[^a-zA-Z0-9\s]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Error downloading file. Please try again.");
    }
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchSkillGapAnalyses();
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedReport(null);
  };

  const fetchSkillGapAnalyses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api1.get(API_ENDPOINTS.GET_SKILL_GAP_ANALYSES);
      
        if (response.data.success) {
          const analyses = response.data.data || [];
          const transformedReports = analyses.map(analysis => {
            const matchingCount = analysis.matchingSkillsCount || 0;
            const gapCount = analysis.skillsGapCount || 0;
            const overallScore = calculateOverallScore(matchingCount, gapCount);
            
            return {
              id: analysis._id,
              role: analysis.targetOccupation || "Unknown Role",
              date: new Date(analysis.createdAt).toLocaleDateString(),
              skillsAssessed: matchingCount + gapCount,
              gapsFound: gapCount,
              overallScore: overallScore,
              scoreColor: getScoreColor(overallScore),
              trend: calculateTrend(matchingCount, gapCount), // Calculate trend based on matching vs gap ratio
              rawData: analysis // Store raw data for potential future use
            };
          });
          
          setPreviousReports(transformedReports);
      } else {
        setError("Failed to fetch skill gap analyses");
      }
    } catch (err) {
      console.error("Error fetching skill gap analyses:", err);
      setError("Error loading assessment history");
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    card: "bg-white rounded-2xl shadow-sm border border-gray-300",
    icon: "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0",
    button: "flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors border border-gray-300 bg-white",
    smallIcon: "w-8 h-8 rounded-lg flex items-center justify-center"
  };

  const whatYoullDiscover = ["Skills you already have vs skills you need to learn", "Your readiness score for the target role", "Specific skills gaps to focus on", "Export results for your learning plan"];
  const howItWorks = ["Choose your target career", "Select your matching skills", "Get detailed gap analysis"];


  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Main Title Section */}
      <div className="flex justify-center mb-3">
        <div className={`${styles.card} p-6 max-w-2xl`}>
          <div className="flex gap-4">
            <div className={styles.icon} style={{ backgroundColor: COLORS.purple }}>
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">Skills Gap Analysis</h1>
              <p className="text-gray-600 text-sm">Identify and bridge the gap between your current skills and career goals</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="space-y-6">
        {/* Start New Analysis Section */}
        <div className={`${styles.card} p-8`}>
          <div className="flex items-start gap-4 mb-6">
            <div className={styles.icon} style={{ backgroundColor: COLORS.purple }}>
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold text-gray-900">Start New Analysis</h2>
                <div className="text-sm text-gray-500">
                  ~15-20 min
                </div>
              </div>
              <p className="text-gray-500 text-medium">Discover your skills gap and create a personalized learning path</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* What You'll Discover */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={styles.smallIcon} style={{ backgroundColor: COLORS.purpleLight }}>
                  <Star className="w-4 h-4" style={{ color: COLORS.purple }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">What You'll Discover</h3>
              </div>
              <ul className="space-y-3">
                {whatYoullDiscover.map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.purple }}></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* How It Works */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className={styles.smallIcon} style={{ backgroundColor: COLORS.purpleLight }}>
                  <Lightbulb className="w-4 h-4" style={{ color: COLORS.purple }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">How It Works</h3>
              </div>
              <ol className="space-y-3">
                {howItWorks.map((step, index) => (
                  <li key={index} className="flex items-center gap-3 text-gray-700">
                    <div className="w-6 h-6 text-white rounded-full flex items-center justify-center text-sm font-medium" style={{ backgroundColor: COLORS.purple }}>
                      {index + 1}
                    </div>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>
          
          <button onClick={handleStartAnalysis} className="w-full text-black border border-gray-400 py-2 px-4 rounded-lg font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl">
            <BarChart3 className="w-5 h-5" />
            Start Skills Gap Analysis
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Assessment History Section */}
        <AssessmentHistory
          previousReports={previousReports}
          isLoading={isLoading}
          error={error}
          onViewReport={handleViewReport}
          onDownloadReport={handleDownloadReport}
          onStartAnalysis={handleStartAnalysis}
        />
      </div>

      <SkillsGapAnalysisToolModal isOpen={isModalOpen} onClose={handleCloseModal} />
      <ViewAnalysisModal 
        isOpen={isViewModalOpen} 
        onClose={handleCloseViewModal} 
        analysisData={selectedReport} 
      />
    </div>
  );
};

export default SkillGapAnalysisPage;
